import { initializeApp, getApp, getApps, FirebaseApp } from "firebase/app";
import { getFirestore, doc, setDoc, onSnapshot, Firestore } from "firebase/firestore";
import { getAuth, signInAnonymously, onAuthStateChanged, Auth, User } from "firebase/auth";
import { AppData } from "../types";

const firebaseConfig = {
  apiKey: process.env.VITE_FIREBASE_API_KEY,
  authDomain: process.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VITE_FIREBASE_APP_ID
};

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let auth: Auth | null = null;

try {
  if (firebaseConfig.apiKey && firebaseConfig.projectId) {
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApp();
    }
    db = getFirestore(app);
    auth = getAuth(app);
    
    // Sign in anonymously to ensure we have a valid session for Firestore rules
    signInAnonymously(auth).catch(console.error);
  }
} catch (error) {
  console.error("Firebase Initialization Failed:", error);
}

export { db, auth };

export const logout = async (): Promise<void> => {
  // No-op for local auth, or we could sign out anonymously but that might break the connection
  // For Roster Check, "logout" just means clearing local storage in the UI
  return Promise.resolve();
};

const DATA_DOC_ID = "main_app_data";
const DATA_COLLECTION = "teetime_settings";

const defaultData: AppData = {
  players: [],
  courses: [],
  rounds: [],
  signups: []
};

export const subscribeToAppData = (callback: (data: AppData) => void) => {
  if (!db || !auth) return () => {};

  let unsubscribeSnapshot: (() => void) | undefined;

  // Function to start Firestore listener once authenticated
  const startFirestoreListener = () => {
    if (unsubscribeSnapshot) return; // Already listening

    const docRef = doc(db!, DATA_COLLECTION, DATA_DOC_ID);
    unsubscribeSnapshot = onSnapshot(docRef, async (docSnap) => {
      if (docSnap.exists()) {
        const remoteData = docSnap.data();
        const cleanData: AppData = {
          players: Array.isArray(remoteData.players) ? remoteData.players : [],
          courses: Array.isArray(remoteData.courses) ? remoteData.courses : [],
          rounds: Array.isArray(remoteData.rounds) ? remoteData.rounds : [],
          signups: Array.isArray(remoteData.signups) ? remoteData.signups : [],
        };
        callback(cleanData);
      } else {
        await setDoc(docRef, defaultData);
        callback(defaultData);
      }
    }, (error) => {
        console.error("Firestore listener error:", error);
    });
  };

  // If already signed in, start immediately
  if (auth.currentUser) {
    startFirestoreListener();
  }

  // Listen for auth changes (this handles the initial sign-in delay)
  const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
    if (user) {
      startFirestoreListener();
    }
  });

  // Return cleanup function
  return () => {
    unsubscribeAuth();
    if (unsubscribeSnapshot) {
      unsubscribeSnapshot();
    }
  };
};

const sanitizeData = (data: any): any => {
  if (Array.isArray(data)) return data.map(sanitizeData);
  if (data !== null && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, sanitizeData(value)])
    );
  }
  return data;
};

export const saveAppData = async (data: AppData) => {
  if (!db) return;
  try {
    const sanitized = sanitizeData(data);
    await setDoc(doc(db, DATA_COLLECTION, DATA_DOC_ID), sanitized);
  } catch (e) {
    console.error("Firestore save error:", e);
  }
};